"use client";

import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, Cpu, Globe, Zap } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const Starfield = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let animationFrameId: number;
    let stars: {
      x: number;
      y: number;
      z: number;
      size: number;
    }[] = [];
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initStars();
    };
    const initStars = () => {
      stars = [];
      const numStars = 400;
      for (let i = 0; i < numStars; i++) {
        stars.push({
          x: Math.random() * canvas.width - canvas.width / 2,
          y: Math.random() * canvas.height - canvas.height / 2,
          z: Math.random() * canvas.width,
          size: Math.random() * 2
        });
      }
    };
    const updateAndDraw = () => {
      ctx.fillStyle = 'rgba(245, 242, 230, 0.1)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      const cx = canvas.width / 2;
      const cy = canvas.height / 2;
      const speed = 0.5;
      stars.forEach(star => {
        star.z -= speed;
        if (star.z <= 0) {
          star.z = canvas.width;
          star.x = Math.random() * canvas.width - canvas.width / 2;
          star.y = Math.random() * canvas.height - canvas.height / 2;
        }
        const x = star.x / star.z * canvas.width + cx;
        const y = star.y / star.z * canvas.height + cy;
        const size = (1 - star.z / canvas.width) * 3 * star.size;
        const alpha = 1 - star.z / canvas.width;
        ctx.fillStyle = `rgba(46, 58, 89, ${alpha * 0.4})`;
        ctx.beginPath();
        ctx.arc(x, y, Math.max(0.1, size), 0, Math.PI * 2);
        ctx.fill();
      });
      animationFrameId = requestAnimationFrame(updateAndDraw);
    };
    window.addEventListener('resize', resizeCanvas);
    resizeCanvas();
    updateAndDraw();
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);
  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none opacity-60" />;
};
const NeuralNetwork = () => {
  const svgRef = useRef<SVGSVGElement>(null);
  useEffect(() => {
    if (!svgRef.current) return;
    const width = svgRef.current.clientWidth;
    const height = svgRef.current.clientHeight;
    const svg = svgRef.current;
    while (svg.firstChild) {
      svg.removeChild(svg.firstChild);
    }
    const numNodes = 40;
    const nodes: Array<{
      x: number;
      y: number;
      id: number;
      group: number;
    }> = [];
    for (let i = 0; i < numNodes; i++) {
      nodes.push({
        id: i,
        group: Math.floor(Math.random() * 3),
        x: Math.random() * width,
        y: Math.random() * height
      });
    }
    const links = [];
    for (let i = 0; i < numNodes; i++) {
      const numLinks = Math.floor(Math.random() * 2) + 1;
      for (let j = 0; j < numLinks; j++) {
        const target = Math.floor(Math.random() * numNodes);
        if (target !== i) {
          links.push({
            source: i,
            target
          });
        }
      }
    }
    const linkGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    linkGroup.setAttribute('stroke', 'var(--color-primary)');
    linkGroup.setAttribute('stroke-opacity', '0.1');
    linkGroup.setAttribute('stroke-width', '1');
    links.forEach((link: {
      source: number;
      target: number;
    }) => {
      const sourcNode = nodes[link.source];
      const targetNode = nodes[link.target];
      const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      line.setAttribute('x1', String(sourcNode.x));
      line.setAttribute('y1', String(sourcNode.y));
      line.setAttribute('x2', String(targetNode.x));
      line.setAttribute('y2', String(targetNode.y));
      linkGroup.appendChild(line);
    });
    svg.appendChild(linkGroup);
    const nodeGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    nodes.forEach(node => {
      const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      circle.setAttribute('cx', String(node.x));
      circle.setAttribute('cy', String(node.y));
      circle.setAttribute('r', '4');
      const fillColor = node.group === 0 ? 'var(--color-primary)' : node.group === 1 ? 'var(--color-secondary)' : 'var(--color-accent-foreground)';
      circle.setAttribute('fill', fillColor);
      circle.setAttribute('fill-opacity', '0.4');
      const animate = document.createElementNS('http://www.w3.org/2000/svg', 'animate');
      animate.setAttribute('attributeName', 'r');
      animate.setAttribute('values', '4;6;4');
      animate.setAttribute('dur', '2s');
      animate.setAttribute('repeatCount', 'indefinite');
      const animateOpacity = document.createElementNS('http://www.w3.org/2000/svg', 'animate');
      animateOpacity.setAttribute('attributeName', 'fill-opacity');
      animateOpacity.setAttribute('values', '0.4;0.6;0.4');
      animateOpacity.setAttribute('dur', '2s');
      animateOpacity.setAttribute('repeatCount', 'indefinite');
      circle.appendChild(animate);
      circle.appendChild(animateOpacity);
      nodeGroup.appendChild(circle);
    });
    svg.appendChild(nodeGroup);
  }, []);
  return <svg ref={svgRef} className="absolute inset-0 w-full h-full pointer-events-none z-0 opacity-30 mix-blend-multiply" />;
};
export default function TheNexGenMission() {
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll();
  const {
    ref: statsRef,
    isVisible: isStatsVisible
  } = useRevealOnScroll();
  return <section data-section-id="43441" id="our-mission" className="bg-background text-foreground relative min-h-screen w-full overflow-hidden py-32 lg:py-48">
      {}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-white/50 to-white z-0" />
      <Starfield />
      <NeuralNetwork />

      <div className="container relative z-10 mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          
          {}
          <div ref={textRef} className={`lg:col-span-7 flex flex-col gap-8 transition-all duration-1000 transform ${isTextVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="inline-flex items-center gap-2 text-secondary font-default text-sm tracking-widest uppercase">
              <span className="w-2 h-2 rounded-full bg-secondary animate-pulse" />
              Trusted Legal Guidance
            </div>

            <h2 className="text-5xl md:text-7xl font-default font-bold tracking-tighter leading-[1.1] text-primary drop-shadow-[0_4px_10px_rgba(46,58,89,0.1)]">
              Restoring <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-secondary via-primary to-secondary">
                Your Freedom.
              </span>
            </h2>

            <div className="space-y-6 text-lg md:text-xl text-primary/80 max-w-2xl leading-relaxed backdrop-blur-sm bg-white/40 p-6 rounded-xl border border-primary/10 shadow-sm">
              <p className="font-default">
                At Nexus Group, our mission is to empower timeshare owners who feel trapped in unfair contracts. We provide professional, trustworthy, and personalized guidance to help you navigate the complexities of timeshare exit.
              </p>
              <p className="font-default text-secondary font-semibold italic text-2xl border-l-4 border-secondary pl-6 py-2">
                "We envision a future where financial freedom is restored, ensuring you can break free from the burden and regain your peace of mind."
              </p>
            </div>

            <div className="flex flex-wrap gap-4 pt-4">
              <Button size="lg" className="bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-xl border-none font-default">
                <Link to="https://quitmytimeshare.com/eligibility-check/">Check Eligibility</Link>
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button size="lg" variant="outline" className="border-primary/20 text-primary hover:bg-primary/5 hover:text-primary backdrop-blur-md font-default">
                <Link to="#">Our Story</Link>
              </Button>
            </div>
          </div>

          {}
          <div ref={statsRef} className={`lg:col-span-5 relative transition-all duration-1000 delay-300 transform ${isStatsVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            {}
            <div className="relative z-10 flex flex-col gap-6">
              {[{
              icon: Cpu,
              title: "Legal Expertise",
              desc: "Strategic legal analysis tailored to your specific timeshare contract and resort."
            }, {
              icon: Zap,
              title: "Personalized Path",
              desc: "A custom roadmap designed to navigate the unique challenges of your exit strategy."
            }, {
              icon: Globe,
              title: "Ethical Solutions",
              desc: "Committed to transparency and integrity, providing honest guidance with no false promises."
            }].map((item, i) => <div key={i} data-index={i} className="group relative overflow-hidden rounded-xl border border-primary/10 bg-white/60 p-6 backdrop-blur-md transition-all hover:bg-white/80 hover:border-secondary/30 hover:shadow-xl">
                  <div className="absolute inset-0 bg-gradient-to-r from-secondary/5 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
                  <div className="relative flex items-start gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-secondary/10 text-secondary ring-1 ring-secondary/30">
                      <item.icon className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="text-xl font-default font-semibold text-primary mb-2">{item.title}</h3>
                      <p className="text-primary/70 text-sm leading-relaxed font-default">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}